/**
 * claimAnonChat — carry the homepage conversation into the account.
 *
 * Every sign-up CTA on the landing page promises "This chat, saved" /
 * "Save this chat — free". Until now nothing moved it: anonymous turns lived
 * in `anon_messages` keyed by a browser-local session id, and signing in
 * dropped the visitor on the dashboard with the conversation gone.
 *
 * Call this right after a successful login / register / OAuth callback, then
 * route to `/app?chat=<conversation_id>` so the user lands back inside the
 * conversation they were already having.
 *
 * Fire-and-forget by contract: any failure returns null and the caller
 * continues to the app as before. Never block sign-in on this.
 */
import api from "@/lib/api";

export const ANON_SESSION_KEY = "rammy_anon_session";

export async function claimAnonChat() {
  let sid = null;
  try {
    sid = localStorage.getItem(ANON_SESSION_KEY);
  } catch (_) {
    return null; // private mode / storage blocked
  }
  if (!sid) return null;

  try {
    const { data } = await api.post("/chat/claim-anon", { anon_session_id: sid });
    if (data?.conversation_id) {
      // Claimed once and only once — drop the local id so a later sign-in
      // (different account, same browser) can't re-adopt this thread.
      try { localStorage.removeItem(ANON_SESSION_KEY); } catch (_) { /* no-op */ }
      return data;
    }
    // Nothing to carry over (visitor never chatted) — clean up quietly.
    try { localStorage.removeItem(ANON_SESSION_KEY); } catch (_) { /* no-op */ }
  } catch (_) {
    /* silent — sign-in must never fail because of this */
  }
  return null;
}

/** Where to send the user after auth, given a claim result. */
export function postAuthPath(claimed) {
  return claimed?.conversation_id ? `/app?chat=${claimed.conversation_id}` : "/app";
}

/**
 * One line confirming what Rammy switched on at sign-up. On the homepage a
 * visitor can't be reached, so a reminder or promise is captured and only
 * becomes real here — saying so out loud is the point.
 */
export function activatedSummary(claimed) {
  const items = claimed?.activated || [];
  if (!items.length) return null;
  const first = items[0];
  const when = first.when ? whenPhrase(first.when) : null;
  const noun = first.kind === "commitment" ? "promise" : "reminder";
  const extra = items.length > 1 ? ` (+${items.length - 1} more)` : "";
  return when
    ? `Your ${noun} “${first.title}” is on for ${when}${extra}`
    : `Your ${noun} “${first.title}” is on${extra}`;
}

function whenPhrase(iso) {
  try {
    const d = new Date(iso);
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const tomorrow = new Date(today.getTime() + 86400000).toDateString() === d.toDateString();
    if (sameDay) return time;
    if (tomorrow) return `${time} tomorrow`;
    return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}, ${time}`;
  } catch (_) {
    return null;
  }
}
